/** An invited practitioner records their licence after joining. */
import * as stylex from "@stylexjs/stylex";
import { useState } from "react";
import type { ClinicSnapshot } from "@lattice/core";
import { LICENCE_STATES } from "@lattice/core";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Banner } from "@lattice/ui/Banner";
import { Button } from "@lattice/ui/Button";
import { focusFirstError } from "@lattice/ui/focusFirstError";
import { Field } from "@lattice/ui/Field";
import { Input } from "@lattice/ui/Input";
import { Select } from "@lattice/ui/Select";
import { recordClinicLicence } from "../api.ts";
import { LeaveGuard } from "./LeaveGuard.tsx";
import { ClinicShell } from "./Shell.tsx";
export function LicenceForm({
  onSaved
}: {
  readonly onSaved: (clinic: ClinicSnapshot) => void;
}) {
  const [licenceNumber, setLicenceNumber] = useState("");
  const [licenceState, setLicenceState] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const [saved, setSaved] = useState(false);

  /* Checked on submit and again as the person types, so an error clears once it is fixed. */
  const blank = (value: string, message: string) => tried && value.trim() === "" ? message : null;
  async function submit() {
    setBusy(true);
    setError(null);
    const result = await recordClinicLicence({
      licenceNumber,
      licenceState
    });
    setBusy(false);
    if (result.status === "ok") {
      setSaved(true);
      onSaved(result.clinic);
    } else setError("We couldn't save the licence. Check the number and state.");
  }
  return <ClinicShell title="Record your licence" description="Lattice staff verify it before this clinic can submit formulas." signedIn width="narrow">
      <LeaveGuard dirty={!saved && (licenceNumber.trim() !== "" || licenceState !== "")} what="the licence you started" />
      <form noValidate className="x78zum5 xdt5ytf x1r0x45p xh8yej3" onSubmit={event => {
      event.preventDefault();
      setTried(true);
      if (licenceNumber.trim() === "" || licenceState === "") focusFirstError(event.currentTarget);else if (!busy) void submit();
    }}>
        <div className="xrvj5dj x1y6fwsi x1r0x45p">
          <Field label="Licence number" id="licence-number" hint="Letters, digits and hyphens, as printed on the licence." error={blank(licenceNumber, "Enter the licence number.")}>
            {control => <Input {...control} size="lg" value={licenceNumber} onChange={setLicenceNumber} />}
          </Field>
          <Field label="State that issued it" id="licence-state" error={blank(licenceState, "Choose the state that issued the licence.")}>
            {control => <Select {...control} size="lg" placeholder="Choose" options={LICENCE_STATES.map(state => ({
            value: state,
            label: state
          }))} value={licenceState} onChange={setLicenceState} />}
          </Field>
        </div>
        {error === null ? null : <Banner tone="danger" title="Not saved">
            {error}
          </Banner>}
        <Button type="submit" testId="save-licence" loading={busy} fullWidth>
          Save licence
        </Button>
      </form>
    </ClinicShell>;
}